import { useSentence } from "@/lib/i18n/content";
import { createFileRoute, Link } from "@tanstack/react-router";
import { FlaskConical, ShieldCheck, Leaf, BookOpen } from "lucide-react";
import { BioAbsorptionMatrix } from "@/components/BioAbsorptionMatrix";
import { guideForIngredientAnchor } from "@/lib/clusters";
import {
  DELIVERY_FORMS,
  listIngredientPages,
  resolveIngredientSlug,
} from "@/lib/ingredientDetail";


export const Route = createFileRoute("/ingredients/")({
  component: IngredientsPage,
  head: () => ({
    meta: [
      { title: "Ingredients & Clinical Research. Bio-Absorb Matrix | TruWe" },
      {
        name: "description",
        content:
          "Every TruWe active, dose and clinical study, magnesium, 40:1 inositol, NAC, silymarin, curcumin, ALA, probiotics, all with Bio-Absorb Matrix delivery support.",
      },
      { property: "og:title", content: "Ingredients & Clinical Research. Bio-Absorb Matrix | TruWe" },
      {
        property: "og:description",
        content:
          "Every active, every dose, every study, the science inside every TruWe formula, with Bio-Absorb Matrix delivery support.",
      },
      { property: "og:url", content: "https://truwe.in/ingredients" },
    ],
    links: [{ rel: "canonical", href: "https://truwe.in/ingredients" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://truwe.in/" },
            { "@type": "ListItem", position: 2, name: "Ingredients & Research", item: "https://truwe.in/ingredients" },
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          name: "TruWe Ingredient Library",
          description:
            "Every TruWe active with its exact delivery form, dose per product and peer-reviewed citations.",
          itemListElement: listIngredientPages().map((ing, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: ing.name,
            url: ing.canonicalUrl,
          })),
        }),
      },
    ],

  }),
});

type Ingredient = {
  name: string;
  dose?: string;
  doseNote?: string;
  summary: string;
};

type ProductBreakdown = {
  handle: string;
  title: string;
  productType: string;
  tagline: string;
  servingInfo: string;
  ingredients: Ingredient[];
};

const PRODUCTS: ProductBreakdown[] = [
  {
    handle: "mag-absorb-pro",
    title: "Mag Absorb Pro",
    productType: "Sleep & Recovery",
    tagline: "5-stage magnesium matrix for deep sleep, calm and muscle recovery.",
    servingInfo: "Once daily · 30-day supply",
    ingredients: [
      {
        name: "Liposomal Magnesium Bisglycinate",
        dose: "200 mg",
        doseNote: "elemental magnesium",
        summary: "Phase 3 deep cellular fusion, gentle on the gut, supports relaxation and sleep onset.",
      },
      {
        name: "Aquamin® Marine Magnesium",
        dose: "100 mg",
        doseNote: "elemental magnesium",
        summary: "Phase 2 sustained multi-mineral release sourced from red marine algae.",
      },
      {
        name: "Magnesium Chloride",
        dose: "60 mg",
        doseNote: "elemental magnesium",
        summary: "Phase 1 immediate ionization for rapid onset of action.",
      },
      {
        name: "Magnesium Aspartate",
        dose: "50 mg",
        doseNote: "elemental magnesium",
        summary: "Phase 2 metabolic intermediate, supports energy production and muscle function.",
      },
      {
        name: "Magnesium Gluconate",
        dose: "30 mg",
        doseNote: "elemental magnesium",
        summary: "Phase 1 high-solubility uptake form.",
      },
      {
        name: "Zinc & Copper Bisglycinate",
        dose: "12.5 mg",
        summary: "Chelated mineral antagonist balancing to prevent depletion.",
      },
      {
        name: "Cofactor Complex (P-5-P / Vitamin D3)",
        dose: "3 mg / 600 IU",
        summary: "Activates magnesium utilization and supports neuromuscular synergy.",
      },
      {
        name: "Bio-Absorb Matrix",
        summary: "A proprietary phytonutrient blend formulated to support absorption of the active ingredients. Paired with liposomal carriers, patented ingredients and chelated minerals, delivery forms with published bioavailability data. Exact composition proprietary.",
      },
    ],
  },
  {
    handle: "ova-balance",
    title: "Ova Balance",
    productType: "Hormonal Balance",
    tagline: "Research-backed PCOS sachet for cycle regularity, ovulation and metabolic health.",
    servingInfo: "1 sachet daily · 30-day supply",
    ingredients: [
      {
        name: "Myo-Inositol",
        dose: "2000 mg",
        summary: "Supports insulin sensitivity, ovarian function and cycle regularity.",
      },
      {
        name: "D-Chiro Inositol",
        dose: "50 mg",
        summary: "Combined in the research-backed 40:1 ratio with Myo-Inositol.",
      },
      {
        name: "Berberine HCl",
        dose: "300 mg",
        summary: "Plant alkaloid that supports healthy blood sugar and metabolic function.",
      },
      {
        name: "Quatrefolic® (L-Methylfolate)",
        dose: "400 mcg",
        summary: "Active folate form, works for everyone, including MTHFR variants.",
      },
      {
        name: "Chromium Picolinate",
        dose: "200 mcg",
        summary: "Supports insulin signaling and balanced cravings.",
      },
      {
        name: "Magnesium Bisglycinate",
        dose: "100 mg",
        doseNote: "elemental magnesium",
        summary: "Chelated mineral co-factor for PMS comfort and relaxation.",
      },
      {
        name: "Zinc Bisglycinate",
        dose: "10 mg",
        summary: "Supports hormonal balance, skin and ovulatory function.",
      },
      {
        name: "Bio-Absorb Matrix",
        summary: "A proprietary phytonutrient blend formulated to support absorption of the active ingredients. Paired with liposomal carriers, patented ingredients and chelated minerals, delivery forms with published bioavailability data. Exact composition proprietary.",
      },
    ],
  },
  {
    handle: "liver-biome-plus",
    title: "Liver Biome Plus",
    productType: "Liver & Gut",
    tagline: "Triple Liposomal liver detox + gut microbiome reset in one daily protocol.",
    servingInfo: "Daily · 30-day supply",
    ingredients: [
      {
        name: "N-Acetyl Cysteine (NAC)",
        dose: "600 mg",
        summary: "Glutathione precursor, the body's master antioxidant. Supports Phase II liver detox.",
      },
      {
        name: "Liposomal Milk Thistle (Silymarin)",
        dose: "350 mg Eq",
        summary: "Liposomal encapsulation for hepatocyte membrane stabilization.",
      },
      {
        name: "Liposomal Alpha-Lipoic Acid",
        dose: "100 mg",
        summary: "Universal antioxidant network, recycles other antioxidants.",
      },
      {
        name: "Liposomal Curcumin",
        dose: "100 mg",
        summary: "Systemic inflammation modulation via lymphatic-bypass delivery.",
      },
      {
        name: "Probiotic Consortium (L. acidophilus / B. lactis)",
        dose: "10 Billion CFU",
        summary: "Enteric-coated to reach the lower intestine intact. Gut-Liver axis support.",
      },
      {
        name: "Bio-Absorb Matrix",
        summary: "A proprietary phytonutrient blend formulated to support absorption of the active ingredients. Paired with liposomal carriers, patented ingredients and chelated minerals, delivery forms with published bioavailability data. Exact composition proprietary.",
      },
    ],
  },
];

function IngredientsPage() {
  const s18n = useSentence();
  return (
    <div className="mx-auto max-w-7xl px-4 py-12 md:px-8 md:py-20">
      <header className="max-w-2xl">
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-brand">The science</p>
        <h1 className="mt-2 font-display text-4xl font-bold text-brand-deep md:text-5xl">
          Product-wise Ingredients
        </h1>
        <p className="mt-3 text-muted-foreground">
          Every TruWe formula is built on research-backed actives at effective doses. No proprietary
          blends, no hand-waving, here's exactly what's inside each bottle.
        </p>
      </header>

      <BioAbsorptionMatrix variant="header" className="mt-12" />

      {/* Quick jump nav */}
      <nav className="mt-8 flex flex-wrap gap-2">
        {PRODUCTS.map((p) => (
          <a
            key={p.handle}
            href={`#${p.handle}`}
            className="rounded-full border border-border bg-card px-4 py-2 text-xs font-semibold text-brand-deep transition hover:border-brand hover:text-brand"
          >
            {p.title}
          </a>
        ))}
      </nav>

      {/* A-Z ingredient index */}
      <section className="mt-14">
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-brand">
          Ingredient library
        </p>
        <h2 className="mt-1 font-display text-2xl font-bold text-brand-deep md:text-3xl">
          Every active, with its exact form and the studies behind it
        </h2>
        <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
          One page per active: the chemical or branded form we use, how it compares with the other
          forms on the market, the dose in each product, and the peer-reviewed citations.
        </p>
        <ul className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {listIngredientPages().map((ing) => (
            <li key={ing.slug}>
              <Link
                to="/ingredients/$slug"
                params={{ slug: ing.slug }}
                className="block h-full rounded-2xl border border-border bg-card p-4 transition hover:border-brand"
              >
                <p className="font-display text-sm font-semibold text-brand-deep">{ing.name}</p>
                <p className="mt-1 text-xs text-brand">{ing.detail.form}</p>
                <p className="mt-1 text-[11px] text-muted-foreground">
                  {ing.skuLabel} · {ing.studyCount}
                </p>
              </Link>
            </li>
          ))}
        </ul>
      </section>

      {/* Delivery forms explainer */}
      <section className="mt-14 rounded-3xl border border-border bg-card p-6 md:p-10">
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-brand">
          Why form matters
        </p>
        <h2 className="mt-1 font-display text-2xl font-bold text-brand-deep">
          Four delivery approaches, chosen ingredient by ingredient
        </h2>
        <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
          The same active can behave very differently depending on the form it arrives in. We pick
          the form per ingredient rather than applying one label-friendly technology to everything.
        </p>
        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          {DELIVERY_FORMS.map((f) => (
            <div key={f.name} className="rounded-2xl border border-border bg-background p-5">
              <h3 className="font-display text-base font-semibold text-brand-deep">{f.name}</h3>
              <p className="mt-1 text-sm text-muted-foreground">{f.what}</p>
              <p className="mt-2 text-sm text-muted-foreground">{f.why}</p>
            </div>
          ))}
        </div>
      </section>



      <div className="mt-12 space-y-16">
        {PRODUCTS.map((product) => (
          <section
            key={product.handle}
            id={product.handle}
            className="scroll-mt-24 rounded-3xl border border-border bg-card p-6 md:p-10"
          >
            <div className="flex flex-col gap-4 border-b border-border pb-6 md:flex-row md:items-end md:justify-between">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-brand">
                  {product.productType}
                </p>
                <h2 className="mt-1 font-display text-2xl font-bold text-brand-deep md:text-3xl">
                  {product.title}
                </h2>
                <p className="mt-2 max-w-xl text-sm text-muted-foreground">{s18n(product.tagline)}</p>
              </div>
              <div className="flex flex-col items-start gap-2 md:items-end">
                <span className="rounded-full bg-brand-soft px-3 py-1 text-xs font-semibold text-brand-deep">
                  {product.servingInfo}
                </span>
                <Link
                  to="/products/$handle"
                  params={{ handle: product.handle }}
                  className="text-xs font-semibold text-brand hover:underline"
                >
                  View product →
                </Link>
                {(() => {
                  const guide = guideForIngredientAnchor(product.handle);
                  if (!guide) return null;
                  return (
                    <a
                      href={`/blog/${guide.slug}`}
                      className="inline-flex items-center gap-1 text-xs font-semibold text-brand-deep hover:text-brand"
                    >
                      <BookOpen className="h-3 w-3" />
                      Read the guide →
                    </a>
                  );
                })()}
              </div>
            </div>

            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              {product.ingredients.map((ing) => (
                <div
                  key={ing.name}
                  className="rounded-2xl border border-border bg-background p-5 transition hover:border-brand/40"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h3 className="font-display text-base font-semibold text-brand-deep">
                        {ing.name}
                      </h3>
                      {ing.dose && (
                        <p className="mt-0.5 text-xs font-semibold uppercase tracking-[0.14em] text-brand">
                          {ing.dose}
                        </p>
                      )}
                      {ing.doseNote && (
                        <p className="text-[10px] text-muted-foreground">{ing.doseNote}</p>
                      )}
                    </div>
                    <FlaskConical className="h-4 w-4 flex-shrink-0 text-brand" />
                  </div>
                  <p className="mt-2 text-sm text-muted-foreground">{ing.summary}</p>
                  {(() => {
                    const slug = resolveIngredientSlug(ing.name);
                    if (!slug) return null;
                    return (
                      <Link
                        to="/ingredients/$slug"
                        params={{ slug }}
                        className="mt-3 inline-block text-xs font-semibold text-brand hover:underline"
                      >
                        Form, dose &amp; studies →
                      </Link>
                    );
                  })()}
                </div>

              ))}
            </div>
          </section>
        ))}
      </div>

      <section className="mt-16 grid gap-4 rounded-3xl bg-brand-soft p-8 md:grid-cols-2 md:p-12">
        <div className="flex items-start gap-4">
          <ShieldCheck className="mt-1 h-6 w-6 flex-shrink-0 text-brand" />
          <div>
            <h2 className="font-display text-xl font-bold text-brand-deep">Tested every batch</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Third-party tested for identity, potency, heavy metals and microbials. Manufactured in
              FSSAI-licensed, GMP-certified, USFDA-registered facilities.
            </p>
          </div>
        </div>
        <div className="flex items-start gap-4">
          <Leaf className="mt-1 h-6 w-6 flex-shrink-0 text-brand" />
          <div>
            <h2 className="font-display text-xl font-bold text-brand-deep">Clean by default</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              No artificial colors, no unnecessary fillers, and every active disclosed with its amount. Vegan capsules
              wherever possible.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
